import React, { useState } from "react"
import { useMutation } from "urql"

import { AnimatedModal } from "../../components/shared/Modal"
import { RadioWithLabel } from "../../components/shared/RadioButtons"
import Typography from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"

import {
  REQUEST_EVENT_REGISTRATION_PAYMENT_MUTATION,
  REQUEST_EVENT_SERIES_REGISTRATION_PAYMENT_MUTATION
} from "./mutations"

export default function RequestEventRegistrationPaymentModal({
  visible,
  hideModal,
  client,
  registration,
  isSeries = false
}) {
  const [sendTo, setSendTo] = useState("email")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const { showToast } = useToast()

  const mutation = isSeries
    ? REQUEST_EVENT_SERIES_REGISTRATION_PAYMENT_MUTATION
    : REQUEST_EVENT_REGISTRATION_PAYMENT_MUTATION
  const fieldName = isSeries ? "requestEventSeriesRegistrationPayment" : "requestEventRegistrationPayment"

  const [, requestPayment] = useMutation(mutation)

  const handleSend = async () => {
    setSending(true)
    setError(null)

    try {
      const result = await requestPayment({
        registrationId: registration.id,
        sendTo
      })

      if (result.data?.[fieldName]?.result === "success") {
        showToast("Payment request sent")
        hideModal()
      } else {
        const errorMessage = result.data?.[fieldName]?.errors?.[0] || "Failed to send payment request"
        setError(errorMessage)
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred")
    } finally {
      setSending(false)
    }
  }

  const hasEmail = Boolean(client?.email)
  const hasPhone = Boolean(client?.phone)

  return (
    <AnimatedModal
      visible={visible}
      hideModal={hideModal}
      header="Request Payment"
      showFooter={true}
      cancelButtonCopy="Cancel"
      cancelButtonType="tertiary"
      actionButtonCopy={sending ? "Sending..." : "Send Request"}
      actionButtonType="primary"
      saveDisabled={sending || (!hasEmail && !hasPhone)}
      onSave={handleSend}>
      <div className="py-4">
        <Typography variant="body" className="mb-4">
          Send a payment link to{" "}
          <strong>
            {client?.firstName} {client?.lastName}
          </strong>
          . They can add a card and pay from their registration page.
        </Typography>

        <Typography variant="body" className="mb-3 font-semibold">
          Send via
        </Typography>

        <RadioWithLabel
          id="send_email"
          name="send_to"
          label={hasEmail ? `Email (${client.email})` : "Email (not available)"}
          checked={sendTo === "email"}
          onChange={() => setSendTo("email")}
          value="email"
          disabled={!hasEmail}
          className="mb-3"
        />
        <RadioWithLabel
          id="send_sms"
          name="send_to"
          label={hasPhone ? `Text (${client.phone})` : "Text (not available)"}
          checked={sendTo === "sms"}
          onChange={() => setSendTo("sms")}
          value="sms"
          disabled={!hasPhone}
          className="mb-3"
        />
        <RadioWithLabel
          id="send_both"
          name="send_to"
          label="Both email and text"
          checked={sendTo === "both"}
          onChange={() => setSendTo("both")}
          value="both"
          disabled={!hasEmail || !hasPhone}
          className="mb-3"
        />

        {error && <div className="mt-2 text-sm text-red">{error}</div>}
      </div>
    </AnimatedModal>
  )
}
